// src/mobile_pages/const/UserTemplate.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Edit,
  FileText,
  User,
  Calendar
} from "lucide-react";
import {
  getUserTemplates,
  deleteUserTemplate,
} from "../../api/const";

export default function UserTemplateList() {
  const { id } = useParams();
  const project = id;
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 데이터 조회
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await getUserTemplates(project, {
        page,
        page_size: pageSize,
      });
      if (Array.isArray(res)) {
        setTemplates(res);
        setTotal(res.length);
      } else {
        setTemplates(res.results);
        setTotal(res.count);
      }
    } catch (error) {
      console.error("템플릿 불러오기 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [page, pageSize]);

  // 삭제
  const handleDelete = async (e, templateId) => {
    e.stopPropagation();
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deleteUserTemplate(templateId);
      fetchTemplates();
    } catch (error) {
      console.error("삭제 실패:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  // 템플릿 선택 시 보고서 작성 페이지로 이동
  const handleRowClick = (template) => {
    navigate(`/project/${project}/const/progress/new`, {
      state: {
        templateId: template.id,
        templateContent: template.template,
        templateTitle: template.title,
        templateType: template.period_type,
      },
    });
  };

  // 편집 페이지로 이동
  const handleEdit = (e, templateId) => {
    e.stopPropagation();
    navigate(`/project/${project}/const/userTemplate/view/${templateId}`);
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(total / pageSize) || 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // 뒤로가기
  const handleBack = () => {
    navigate(`/project/${project}/const/progress`);
  };

  // 새 템플릿 작성
  const handleNew = () => {
    navigate(`/project/${project}/const/userTemplate/new`);
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 주/월간 보고서</p>
          <h1 className="text-xl font-bold text-slate-900">사용자 템플릿</h1>
          <p className="mt-1 text-xs text-slate-500">
            총 {total}개의 템플릿이 등록되어 있습니다
          </p>
        </header>

        {/* 안내 카드 */}
        <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100">
          <p className="text-[12px] text-indigo-800">
            💡 템플릿을 선택하면 해당 내용으로 보고서를 작성할 수 있습니다.
          </p>
        </div>

        {/* 목록 */}
        <section className="space-y-3">
          {loading ? (
            <p className="py-12 text-center text-[15px] text-slate-400">
              불러오는 중...
            </p>
          ) : templates.length === 0 ? (
            <div className="py-12 text-center">
              <FileText size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-[15px] text-slate-400">등록된 템플릿이 없습니다</p>
              <p className="text-xs text-slate-400 mt-1">새 템플릿을 등록해보세요</p>
            </div>
          ) : (
            templates.map((template) => (
              <article
                key={template.id}
                onClick={() => handleRowClick(template)}
                className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
              >
                {/* 상단: 종류 배지 + 액션 버튼들 */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${template.period_type === "monthly"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-blue-100 text-blue-700"
                    }`}>
                    {template.period_type === "monthly" ? "월간" : "주간"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleEdit(e, template.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, template.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* 제목 */}
                <h3 className="text-[15px] font-semibold text-slate-800 line-clamp-2 mb-2">
                  {template.title || "(제목 없음)"}
                </h3>

                {/* 메타 정보 */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {template.created_by?.name || "-"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {template.created_date
                      ? new Date(template.created_date).toLocaleDateString("ko-KR")
                      : "-"}
                  </span>
                </div>

                {/* 선택 화살표 */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <ChevronRight size={20} className="text-slate-300" />
                </div>
              </article>
            ))
          )}
        </section>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <nav className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => p - 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canPrev
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${page === pageNum
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 active:bg-slate-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className={`flex h-9 w-9 items-center justify-center rounded-full border ${canNext
                ? "border-slate-300 text-slate-600 active:scale-95"
                : "border-slate-100 text-slate-300"
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            보고서 목록
          </button>
          <button
            type="button"
            onClick={handleNew}
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            새 템플릿
          </button>
        </div>
      </footer>
    </div>
  );
}
